import { useState } from 'react';
import { Link } from 'react-router-dom';
import AuthLayout from '@/components/auth/AuthLayout';
import { getActiveRegionConfig, getPortalHeaders } from '@/lib/regionConfig';
import supabase from '@/lib/supabaseClient';

const API_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:8001';

const PendingApproval = () => {
  const regionConfig = getActiveRegionConfig();
  const [resendState, setResendState] = useState('idle');
  const [resendMessage, setResendMessage] = useState('');

  const resendAdminNotification = async () => {
    if (resendState === 'sending') return;
    setResendState('sending');
    setResendMessage('');

    const { data: { session } } = await supabase.auth.getSession();
    const accessToken = session?.access_token;
    if (!accessToken) {
      setResendState('error');
      setResendMessage('Session expired. Please sign in again and request approval reminder.');
      return;
    }

    try {
      const resp = await fetch(`${API_URL}/api/auth/post-verify`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${accessToken}`, ...getPortalHeaders(regionConfig) },
      });

      if (!resp.ok) {
        setResendState('error');
        setResendMessage('Could not notify admin right now. Please try again in a minute.');
        return;
      }

      const payload = await resp.json();
      if (payload?.admin_email_sent === false) {
        setResendState('error');
        setResendMessage(`Notification attempt failed on server. Please contact ${regionConfig.supportEmail}.`);
        return;
      }

      setResendState('sent');
      setResendMessage('Admin notification sent. We will review your account shortly.');
    } catch {
      setResendState('error');
      setResendMessage('Network issue while notifying admin. Please try again in a minute.');
    }
  };

  return (
    <AuthLayout>
      <div className="w-full max-w-md text-center">

        <div className="mb-8 lg:hidden">
          <img src="/logo-full.png" alt="Digital Reporting" className="h-10 mx-auto" />
        </div>

        <div className="bg-card border border-border rounded-lg p-10">
          {/* Hourglass / pending icon */}
          <div className="w-14 h-14 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-6">
            <svg className="w-7 h-7 text-primary" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.75}
                d="M12 6v6l4 2m6-2a10 10 0 1 1-20 0 10 10 0 0 1 20 0z" />
            </svg>
          </div>

          <h1 className="text-xl font-semibold mb-3">Account pending approval</h1>

          <p className="text-sm text-muted-foreground leading-relaxed mb-6">
            Your email address has been verified. A member of our team will review your
            registration shortly and you will receive a confirmation email once your
            account is approved.
          </p>

          <div className="bg-muted/40 rounded-md p-4 text-left text-sm text-muted-foreground space-y-2 mb-8">
            <div className="flex items-start gap-2">
              <span className="mt-0.5 flex-shrink-0 w-5 h-5 rounded-full bg-primary text-primary-foreground text-xs font-bold flex items-center justify-center">1</span>
              <span>Email verified ✓</span>
            </div>
            <div className="flex items-start gap-2">
              <span className="mt-0.5 flex-shrink-0 w-5 h-5 rounded-full bg-muted-foreground/20 text-muted-foreground text-xs font-bold flex items-center justify-center">2</span>
              <span>Account reviewed by our team — <em>in progress</em></span>
            </div>
            <div className="flex items-start gap-2">
              <span className="mt-0.5 flex-shrink-0 w-5 h-5 rounded-full bg-muted-foreground/20 text-muted-foreground text-xs font-bold flex items-center justify-center">3</span>
              <span>Confirmation email sent to you</span>
            </div>
            <div className="flex items-start gap-2">
              <span className="mt-0.5 flex-shrink-0 w-5 h-5 rounded-full bg-muted-foreground/20 text-muted-foreground text-xs font-bold flex items-center justify-center">4</span>
              <span>Sign in and get started</span>
            </div>
          </div>

          <p className="text-xs text-muted-foreground">
            Questions?{' '}
            <a
              href={`mailto:${regionConfig.supportEmail}`}
              className="font-medium text-primary hover:underline"
            >
              {regionConfig.supportEmail}
            </a>
          </p>

          <div className="mt-5">
            <button
              type="button"
              onClick={resendAdminNotification}
              disabled={resendState === 'sending' || resendState === 'sent'}
              className="text-sm font-medium text-primary hover:underline disabled:opacity-50"
            >
              {resendState === 'sending' ? 'Sending reminder...' : resendState === 'sent' ? 'Reminder sent' : 'Resend admin notification'}
            </button>
            {resendMessage && (
              <p className={`text-xs mt-2 ${resendState === 'error' ? 'text-destructive' : 'text-muted-foreground'}`}>
                {resendMessage}
              </p>
            )}
          </div>
        </div>

        <p className="text-center text-sm text-muted-foreground mt-6">
          Already approved?{' '}
          <Link to="/login" className="font-medium text-primary hover:underline">
            Sign in
          </Link>
        </p>

      </div>
    </AuthLayout>
  );
};

export default PendingApproval;
